import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, TextField, Button, Paper, Radio, RadioGroup, FormControlLabel, Chip, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { calculateLoanPayments, LoanPayment } from "../../services/loanCalculator";
import LoanPaymentsTable from "./LoanPaymentsTable";
import LoanPaymentsChart from "./LoanPaymentsChart";
import CalculateIcon from "@mui/icons-material/Calculate";
import SaveIcon from "@mui/icons-material/Save";
import { InterestType, LoanDetails, RepaymentType, LinkageType, RepaymentFrequency } from "../../interfaces/DTO/mislaka_xml_models";
import { calculateRemainingMonths } from "../../utils/dateCalculators";
import { formatCurrency } from "../../utils/namesFormater";
import { formatRepaymentType, formatLinkageType, formatRepaymentFrequency } from "../../utils/enumFormatter";
import CurrencyTextField from "../../shared/UIParts/InputFields/CurrencyTextField";

interface LoanCalculatorProps {
  loanDetails?: LoanDetails;
  autoCalculate?: boolean; // אם true, יחשב אוטומטית בטעינה
  reportedPeriod?: string; // תקופה מדווחת (ISO date)
  mode?: 'view' | 'edit'; // מצב תצוגה או עריכה
  onSave?: (updatedLoanDetails: LoanDetails) => void | Promise<void>; // callback לשמירת שינויים
}

const LoanCalculator: React.FC<LoanCalculatorProps> = ({
  loanDetails,
  autoCalculate = false,
  reportedPeriod,
  mode = 'view',
  onSave,
}) => {
  
  // אם יש loanDetails זו הלוואה קיימת, אחרת זו סימולציה
  const isExistingLoan = !!loanDetails;
  // חישוב ערכים התחלתיים מ-loanDetails
  const totalLoanMonths = loanDetails?.loan_start_date && loanDetails?.loan_end_date
    ? calculateRemainingMonths(loanDetails.loan_start_date, loanDetails.loan_end_date)
    : undefined;

  const [loanAmount, setLoanAmount] = useState<number>(loanDetails?.loan_amount || 0);
  const [months, setMonths] = useState<string>(totalLoanMonths?.toString() || "");
  const [interestRate, setInterestRate] = useState<string>((loanDetails?.interest_rate || 5.5).toString());
  const [interestType, setInterestType] = useState<InterestType>(loanDetails?.interest_type || InterestType.FIXED);
  const [repaymentType, setRepaymentType] = useState<RepaymentType>(
    loanDetails?.repayment_type || RepaymentType.CURRENT_INTEREST
  );
  const [linkageType, setLinkageType] = useState<LinkageType>(loanDetails?.linkage_type || LinkageType.NONE);
  const [repaymentFrequency, setRepaymentFrequency] = useState<RepaymentFrequency>(
    loanDetails?.repayment_frequency || RepaymentFrequency.MONTHLY
  );
  const [isSaving, setIsSaving] = useState(false);

  // האם נמצאים במצב עריכה
  const isEditMode = mode === 'edit';

  // חישוב אוטומטי בכל שינוי
  const payments = useMemo(() => {
    const amount = loanAmount;
    const term = parseInt(months);
    const rate = parseFloat(interestRate);

    if (isNaN(amount) || isNaN(term) || isNaN(rate) || amount <= 0 || term <= 0 || rate < 0) {
      return [];
    }

    const grace = repaymentType === RepaymentType.CURRENT_INTEREST ? term : 0;

    return calculateLoanPayments({
      loanAmount: amount,
      months: term,
      annualInterestRate: rate,
      gracePeriod: grace,
    });
  }, [loanAmount, months, interestRate, repaymentType]);

  // בדיקה אם זו הלוואת בלון (גרייס מלא - רק ריבית ללא החזר קרן)
  const isBalloonLoan = repaymentType === RepaymentType.CURRENT_INTEREST;

  // חישוב החזר חודשי מוערך
  const estimatedMonthlyPayment = useMemo(() => {
    if (payments.length === 0) return null;

    // בהלוואת ריבית שוטפת - החזר הריבית (כל החודשים מלבד האחרון זהים)
    if (isBalloonLoan) {
      return payments[0]?.totalPayment || 0;
    }

    // בשפיצר - התשלום הקבוע (כל החודשים זהים)
    return payments[0]?.totalPayment || 0;
  }, [payments, isBalloonLoan]);

  // חישוב אוטומטי בטעינה ראשונית (אם יש loanDetails)
  useEffect(() => {
    // אין צורך ב-handleCalculate כי useMemo כבר מחשב אוטומטית
  }, [autoCalculate]);

  const handleSave = async () => {
    if (!onSave) return;

    const monthsNum = parseInt(months);
    if (isNaN(monthsNum) || monthsNum <= 0) {
      alert('נא למלא מספר חודשים תקין');
      return;
    }

    setIsSaving(true);
    try {
      // חישוב תאריך סיום
      const startDate = loanDetails?.loan_start_date || new Date().toISOString().split('T')[0];
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + monthsNum);

      // בניית LoanDetails מעודכן
      const updatedLoanDetails: LoanDetails = {
        ...loanDetails,
        loan_amount: loanAmount,
        loan_balance: loanDetails?.loan_balance || loanAmount, // אם אין יתרה, נשתמש בסכום המלא
        loan_start_date: startDate,
        loan_end_date: endDate.toISOString().split('T')[0],
        loan_period_months: monthsNum,
        interest_rate: parseFloat(interestRate),
        interest_type: interestType,
        repayment_type: repaymentType,
        linkage_type: linkageType,
        repayment_frequency: repaymentFrequency,
        periodic_repayment_amount: estimatedMonthlyPayment || 0,
      };

      await onSave(updatedLoanDetails);
    } catch (error) {
      console.error('Failed to save loan details:', error);
      alert('שגיאה בשמירת פרטי ההלוואה');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCalculate = (type: RepaymentType) => {
    setRepaymentType(type);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto", direction: "rtl" }}>
      <Typography variant="h4" sx={{ mb: 4, textAlign: "center", fontWeight: "bold" }}>
        מחשבון הלוואה
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* שורה ראשונה - סכום ההלוואה */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: "120px", textAlign: "right" }}>מה סכום ההלוואה?</Typography>
            <CurrencyTextField
              value={loanAmount}
              onChange={setLoanAmount}
            />
          </Box>

          {/* שורה שנייה - חודשים וריבית והחזר מוערך */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: "120px", textAlign: "right" }}>לכמה חודשים?</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                type="number"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                sx={{ width: "120px" }}
              />
              {!months && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    label="3 שנים"
                    onClick={() => setMonths((3 * 12).toString())}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="5 שנים"
                    onClick={() => setMonths((5 * 12).toString())}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}
            </Box>
            <Typography sx={{ ml: 3, minWidth: "90px", textAlign: "right" }}>ריבית שנתית</Typography>
            <TextField
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              sx={{ width: "180px" }}
              slotProps={{
                input: {
                  endAdornment: <Typography sx={{ ml: 1 }}>%</Typography>,
                },
                htmlInput: {
                  step: "0.1"
                }
              }}
            />
            {estimatedMonthlyPayment !== null && (
              <Box sx={{ ml: 3 }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                  החזר חודשי מוערך: {formatCurrency(estimatedMonthlyPayment)}
                </Typography>
                {loanDetails?.periodic_repayment_amount >= 0 &&
                 Math.abs(loanDetails.periodic_repayment_amount - estimatedMonthlyPayment) > 1 && (
                  <>
                    <Typography sx={{  color: "warning.main", mt: 0.5 }}>
                      החזר חודשי מדווח: {formatCurrency(loanDetails.periodic_repayment_amount)}
                    </Typography>
                    {(loanDetails.periodic_repayment_amount === 0 || parseInt(months) === 1) && (
                      <Typography sx={{ fontSize: "0.85rem", color: "info.main", mt: 0.5, fontStyle: "italic" }}>
                        כנראה הלוואת בלון - כל הסכום ישולם בסוף התקופה
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* שורה שלישית - סוג ריבית */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: "120px", textAlign: "right" }}>סוג ריבית</Typography>
            <RadioGroup
              row
              value={interestType}
              onChange={(e) => setInterestType(parseInt(e.target.value) as InterestType)}
            >
              <FormControlLabel value={InterestType.FIXED} control={<Radio />} label="קבועה" />
              <FormControlLabel value={InterestType.VARIABLE} control={<Radio />} label="משתנה" />
            </RadioGroup>
          </Box>

          {isExistingLoan && (
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* תדירות החזר */}
              {isEditMode ? (
                <FormControl fullWidth>
                  <InputLabel>תדירות החזר</InputLabel>
                  <Select
                    value={repaymentFrequency}
                    label="תדירות החזר"
                    onChange={(e) => setRepaymentFrequency(e.target.value as RepaymentFrequency)}
                  >
                    <MenuItem value={RepaymentFrequency.MONTHLY}>חודשי</MenuItem>
                    <MenuItem value={RepaymentFrequency.QUARTERLY}>רבעוני</MenuItem>
                    <MenuItem value={RepaymentFrequency.SEMI_ANNUAL}>חצי שנתי</MenuItem>
                    <MenuItem value={RepaymentFrequency.ANNUAL}>שנתי</MenuItem>
                    <MenuItem value={RepaymentFrequency.OTHER}>אחר</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="תדירות החזר"
                  value={formatRepaymentFrequency(repaymentFrequency)}
                  fullWidth
                  disabled
                  slotProps={{
                    input: {
                      readOnly: true,
                    }
                  }}
                />
              )}

              {/* סוג הצמדה */}
              {isEditMode ? (
                <FormControl fullWidth>
                  <InputLabel>סוג הצמדה</InputLabel>
                  <Select
                    value={linkageType}
                    label="סוג הצמדה"
                    onChange={(e) => setLinkageType(e.target.value as LinkageType)}
                  >
                    <MenuItem value={LinkageType.NONE}>ללא הצמדה</MenuItem>
                    <MenuItem value={LinkageType.MONTHLY_INDEX}>מדד חודשי</MenuItem>
                    <MenuItem value={LinkageType.SEMI_ANNUAL_INDEX}>מדד חצי שנתי</MenuItem>
                    <MenuItem value={LinkageType.OTHER}>אחר</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="סוג הצמדה"
                  value={formatLinkageType(linkageType)}
                  fullWidth
                  disabled
                  slotProps={{
                    input: {
                      readOnly: true,
                    }
                  }}
                />
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant={repaymentType === RepaymentType.SPITZER ? "contained" : "outlined"}
              size="large"
              onClick={() => handleCalculate(RepaymentType.SPITZER)}
              fullWidth
              sx={{
                py: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                textAlign: "right",
                textTransform: "none",
                flex: 1,
                border: repaymentType === RepaymentType.SPITZER ? "2px solid" : "1px solid",
                borderColor: repaymentType === RepaymentType.SPITZER ? "primary.main" : "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <CalculateIcon />
                <Typography variant="h6" fontWeight="bold">
                  {formatRepaymentType(RepaymentType.SPITZER)}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontSize: "0.75rem", opacity: 0.9, lineHeight: 1.4 }}>
                תשלום קבוע הכולל גם ריבית וגם חלק מהקרן.
                עם הזמן, חלק הריבית בתשלום קטן, וחלק הקרן גדל
              </Typography>
            </Button>

            <Button
              variant={repaymentType === RepaymentType.CURRENT_INTEREST ? "contained" : "outlined"}
              size="large"
              onClick={() => handleCalculate(RepaymentType.CURRENT_INTEREST)}
              fullWidth
              color="secondary"
              sx={{
                py: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                textAlign: "right",
                textTransform: "none",
                flex: 1,
                border: repaymentType === RepaymentType.CURRENT_INTEREST ? "2px solid" : "1px solid",
                borderColor: repaymentType === RepaymentType.CURRENT_INTEREST ? "secondary.main" : "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <CalculateIcon />
                <Typography variant="h6" fontWeight="bold">
                  {formatRepaymentType(RepaymentType.CURRENT_INTEREST)}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontSize: "0.75rem", opacity: 0.9, lineHeight: 1.4 }}>
                משלמים רק את הריבית לאורך תקופת ההלוואה,
                והקרן כולה נפרעת בסוף התקופה (בתשלום אחד)
              </Typography>
            </Button>
          </Box>

          {/* כפתור שמירה במצב עריכה */}
          {isEditMode && onSave && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSave}
                disabled={isSaving || payments.length === 0}
                startIcon={<SaveIcon sx={{ ml: 1 }} />}
                sx={{ minWidth: 200 }}
              >
                {isSaving ? "שומר..." : "שמור פרטי הלוואה"}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {payments.length > 0 && (
        <>
          <LoanPaymentsChart
            payments={payments}
            startDate={loanDetails?.loan_start_date}
            endDate={loanDetails?.loan_end_date}
            isExistingLoan={isExistingLoan}
            interestType={interestType}
            repaymentType={repaymentType}
          />
          <LoanPaymentsTable
            payments={payments}
            startDate={loanDetails?.loan_start_date}
            endDate={loanDetails?.loan_end_date}
            interestType={interestType}
            interestRate={parseFloat(interestRate)}
            isExistingLoan={isExistingLoan}
            repaymentType={repaymentType}
            loanDetails={loanDetails}
            reportedPeriod={reportedPeriod}
          />
        </>
      )}
    </Box>
  );
};

export default LoanCalculator;
